import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { formatMoney } from '@belong/common';
import { Divider, Text, Toast } from '@belong/ui';
import {
  MaintenanceQuoteConsentStatus,
  MaintenanceQuoteConsumerModel,
  MaintenanceQuoteRejectionReason,
  QuoteTypeOfWorkLabels,
} from 'maintenance/types/quote-centric';
import { updateQuoteCentricConsent } from 'store/redux/maintenance/actions';
import clsx from 'clsx';
import { QUOTE_APPROVAL_STATUS_STYLES } from 'maintenance/constants';
import { STRINGS } from 'maintenance/strings';
import { isQuoteApproved, isQuoteRejected } from 'maintenance/utils';
import { CostItemsGroup } from './CostItemsGroup';
import { QuoteDecisionFooter } from './QuoteDecisionFooter';
import { AssignedProSection } from './QuoteScopeOfWorkSections';

type QuoteDetailsProps = {
  maintenanceUniqueId: string;
  quote: MaintenanceQuoteConsumerModel;
  onQuotesChange: () => Promise<void>;
  hasMultipleQuotes?: boolean;
};

export const LegacyQuoteDetails = ({
  maintenanceUniqueId,
  quote,
  onQuotesChange,
  hasMultipleQuotes,
}: QuoteDetailsProps) => {
  const dispatch = useDispatch();
  const [isUpdating, setIsUpdating] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const toastMessage = useRef('');

  const isRejected = isQuoteRejected(quote);
  const isApproved = !isRejected && isQuoteApproved(quote);
  const needsMyApproval = quote.myApproval?.consentStatus === 'Required';
  const tag = isRejected
    ? 'Rejected'
    : isApproved
      ? 'Approved'
      : needsMyApproval
        ? 'Pending Approval'
        : undefined;

  const canDecide =
    quote.status !== 'Rejected' &&
    quote.status !== 'NotSelected' &&
    !quote.approvals.some((a) => a.consentStatus === 'Rejected') &&
    quote.myApproval?.consentStatus === 'Required';

  const submitConsent = async (
    payload: {
      isApproved: boolean;
      rejectionReason?: MaintenanceQuoteRejectionReason;
      notes?: string;
    },
    successMessage: string,
  ) => {
    let isSubmitted = false;
    try {
      setIsUpdating(true);
      await dispatch(
        updateQuoteCentricConsent(maintenanceUniqueId, quote.uniqueId, payload),
      );
      isSubmitted = true;
      toastMessage.current = successMessage;
      setToastVisible(true);
      await onQuotesChange();
    } catch (e) {
      console.error(e);
      toastMessage.current = STRINGS['quote-submit-error'];
      setToastVisible(true);
    } finally {
      setIsUpdating(false);
    }
    return isSubmitted;
  };

  const handleApprove = () =>
    submitConsent({ isApproved: true }, STRINGS['quote-approved']);

  const handleQuestionsSubmit = (
    rejectionReason: MaintenanceQuoteRejectionReason,
    notes?: string,
  ) =>
    submitConsent(
      { isApproved: false, rejectionReason, notes },
      STRINGS['quote-response-submitted'],
    );

  const myParty = quote.myApproval?.party ?? null;
  const myPercentage = quote.myApproval?.percentage ?? 0;
  const myAmount =
    quote.myApproval?.estimatedAmount ?? quote.myEstimatedAmount ?? 0;

  // A discount is stored as a negative-value `Discount` cost item. The total is
  // already net of it; surface it as its own line so the savings are visible.
  const discountItem = quote.costItems.find(
    (item) => item.type === 'Discount' && item.cost < 0,
  );
  const discountAmount = discountItem ? Math.abs(discountItem.cost) : 0;

  const otherApproval = quote.approvals.find(
    (a) => a.party !== myParty && a.party !== 'Belong' && a.percentage > 0,
  );
  const otherPercentage = otherApproval?.percentage ?? 0;
  const otherAmount = otherApproval
    ? quote.totalAmount * (otherApproval.percentage / 100)
    : 0;
  const otherPartyLabel = otherApproval?.party;
  const myConsentStatus = quote.myApproval?.consentStatus;
  const otherDifferentApprovals = quote.approvals.filter(
    (
      a,
    ): a is typeof a & {
      consentStatus: Exclude<MaintenanceQuoteConsentStatus, 'NotRequired'>;
    } =>
      a.party !== myParty &&
      a.party !== 'Belong' &&
      a.consentStatus !== 'NotRequired' &&
      a.consentStatus !== myConsentStatus,
  );
  const displayApprovals =
    otherDifferentApprovals.length > 0 &&
    myParty &&
    myConsentStatus &&
    myConsentStatus !== 'NotRequired'
      ? [
          { party: myParty, consentStatus: myConsentStatus },
          ...otherDifferentApprovals,
        ]
      : otherDifferentApprovals;

  return (
    <div>
      <div
        className={
          hasMultipleQuotes
            ? 'p-sm'
            : 'rounded-t border border-solid border-gray bg-light-gray px-md pt-md pb-xl'
        }
      >
        {!hasMultipleQuotes && tag && (
          <span
            className={clsx(
              'mb-xs inline-block whitespace-nowrap rounded-small px-xs text-p1 text-white',
              isApproved ? 'bg-green' : isRejected ? 'bg-dark-gray' : 'bg-red',
            )}
          >
            {tag}
          </span>
        )}

        <div className="flex items-start justify-between gap-sm">
          <Text fontWeight="semibold">
            {quote.title}
            {quote.typeOfWork
              ? ` • ${QuoteTypeOfWorkLabels[quote.typeOfWork]}`
              : ''}
          </Text>
        </div>

        {displayApprovals.length > 0 && (
          <div className="mt-xs flex flex-wrap gap-x-sm gap-y-2xs border-0 border-t border-dashed border-gray py-xs">
            {displayApprovals.map((a) => {
              const style = QUOTE_APPROVAL_STATUS_STYLES[a.consentStatus];
              const { Icon } = style;
              return (
                <div
                  key={a.party}
                  className={clsx('flex items-center gap-2xs', style.className)}
                >
                  <Icon className="shrink-0" width={12} height={12} />
                  <Text fontWeight="semibold" variant="p1">
                    {style.label(a.party !== myParty ? a.party : undefined)}
                  </Text>
                </div>
              );
            })}
          </div>
        )}

        {quote.scopeOfWork && (
          <Text className="mt-sm">{quote.scopeOfWork}</Text>
        )}

        {quote.costItems.length > 0 && (
          <div className="mt-sm">
            <CostItemsGroup costItems={quote.costItems} />
          </div>
        )}

        {quote.assignedPro?.name && (
          <AssignedProSection name={quote.assignedPro.name} className="mt-xl" />
        )}

        <Divider borderStyle="dashed" className="my-sm" />

        <div className="flex justify-between">
          <Text fontWeight="semibold">Subtotal</Text>
          <Text fontWeight="semibold" className="text-navy">
            {formatMoney(quote.totalAmount + discountAmount)}
          </Text>
        </div>

        {otherPercentage > 0 && otherPartyLabel && (
          <div className="mt-xs flex justify-between">
            <Text>
              {otherPartyLabel} Responsibility ({otherPercentage}%)
            </Text>
            <Text>{formatMoney(otherAmount)}</Text>
          </div>
        )}

        {discountAmount > 0 && (
          <div className="mt-xs flex justify-between">
            <Text fontWeight="semibold" className="text-green">
              In-Network Discount
            </Text>
            <Text fontWeight="semibold" className="text-green">
              {`-${formatMoney(discountAmount)}`}
            </Text>
          </div>
        )}

        {myPercentage > 0 ? (
          <div className="mt-xs flex justify-between">
            <Text fontWeight="semibold">Your Portion ({myPercentage}%)</Text>
            <div className="flex items-end gap-xs">
              {discountAmount > 0 && (
                <Text variant="p1" className="text-dark-gray line-through">
                  {formatMoney(
                    (quote.totalAmount + discountAmount) * (myPercentage / 100),
                  )}
                </Text>
              )}
              <Text fontWeight="semibold" className="text-navy">
                {formatMoney(myAmount)}
              </Text>
            </div>
          </div>
        ) : (
          <div className="mt-xs flex justify-between">
            <Text fontWeight="semibold">Your Portion (0%)</Text>
            <Text fontWeight="semibold" className="text-navy">
              Free
            </Text>
          </div>
        )}
      </div>
      {canDecide && (
        <QuoteDecisionFooter
          isUpdating={isUpdating}
          onApprove={handleApprove}
          onQuestionsSubmit={handleQuestionsSubmit}
        />
      )}

      <Toast
        autocloseDuration={5000}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
      >
        {toastMessage.current}
      </Toast>
    </div>
  );
};
